import { useTranslation } from 'i18n';
import { Stack } from 'ui';

import { formatSampleValue } from '../../utils/formatSampleValue/formatSampleValue';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import type { Item } from '../MaterialSampleBreakdown/MaterialSampleBreakdown';

const PET_FLAKE_FIELDS = [
  { dataKey: 'Sampled by', labelKey: 'breakdowns.petFlake.fields.sampledBy' },
  { dataKey: 'Intrinsic viscosity', labelKey: 'breakdowns.petFlake.fields.intrinsicViscosity' },
  { dataKey: 'Clear flakes', labelKey: 'breakdowns.petFlake.fields.clearFlakes' },
  { dataKey: 'Light blue flakes', labelKey: 'breakdowns.petFlake.fields.lightBlueFlakes' },
  { dataKey: 'Dark blue flakes', labelKey: 'breakdowns.petFlake.fields.darkBlueFlakes' },
  { dataKey: 'Polyethylene terephthalate (PET) content', labelKey: 'breakdowns.petFlake.fields.petContent' },
  { dataKey: 'PVC contamination', labelKey: 'breakdowns.petFlake.fields.pvcContamination' },
  { dataKey: 'Polyolefin contamination', labelKey: 'breakdowns.petFlake.fields.polyolefinContamination' },
  { dataKey: 'Residual contamination', labelKey: 'breakdowns.petFlake.fields.residualContamination' },
  { dataKey: 'Glue', labelKey: 'breakdowns.petFlake.fields.glue' },
  { dataKey: 'Residual moisture', labelKey: 'breakdowns.petFlake.fields.residualMoisture' },
  { dataKey: 'Particles with size < 5 mm', labelKey: 'breakdowns.petFlake.fields.particlesSizeLt5mm' },
  {
    dataKey: 'Particles with size between 5 and 10 mm',
    labelKey: 'breakdowns.petFlake.fields.particlesSizeBetween5And10mm',
  },
  {
    dataKey: 'Particles with size between 10 and 12 mm',
    labelKey: 'breakdowns.petFlake.fields.particlesSizeBetween10And12mm',
  },
  { dataKey: 'Bulk density', labelKey: 'breakdowns.petFlake.fields.bulkDensity' },
  { dataKey: 'Yellow flakes', labelKey: 'breakdowns.petFlake.fields.yellowFlakes' },
  { dataKey: 'Green flakes', labelKey: 'breakdowns.petFlake.fields.greenFlakes' },
  { dataKey: 'Coloured flakes', labelKey: 'breakdowns.petFlake.fields.colouredFlakes' },
  { dataKey: 'Ferrous metal', labelKey: 'breakdowns.petFlake.fields.ferrousMetal' },
  { dataKey: 'Non-ferrous metal', labelKey: 'breakdowns.petFlake.fields.nonFerrousMetal' },
  { dataKey: 'Paper', labelKey: 'breakdowns.petFlake.fields.paper' },
  { dataKey: 'Wooden materials', labelKey: 'breakdowns.petFlake.fields.woodenMaterials' },
  { dataKey: 'Other impurities', labelKey: 'breakdowns.petFlake.fields.otherImpurities' },
  { dataKey: 'pH', labelKey: 'breakdowns.petFlake.fields.ph' },
  { dataKey: 'Isophthalic acid', labelKey: 'breakdowns.petFlake.fields.isophthalicAcid' },
  { dataKey: 'Melt flow rate (MF) index (260°C/2,16Kg)', labelKey: 'breakdowns.petFlake.fields.meltFlowRate' },
  { dataKey: 'Melting point', labelKey: 'breakdowns.petFlake.fields.meltingPoint' },
] as const;

export const PetFlakeMaterialSampleBreakdown = ({
  breakdown,
  precision = 1,
}: {
  breakdown: Record<string, Item>;
  precision?: number;
}) => {
  const { t } = useTranslation('features');
  const renderField = (dataKey: string, label: string) => {
    return (
      <MaterialBreakdownItem
        key={dataKey}
        label={label}
        value={breakdown[dataKey] ? formatSampleValue(breakdown[dataKey], precision) : '-'}
      />
    );
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      {PET_FLAKE_FIELDS.map(({ dataKey, labelKey }) => renderField(dataKey, t(labelKey)))}
    </Stack>
  );
};
